import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import React, { useCallback, useState } from "react";

import { formatSkillRelativePathForDisplay } from "../utils/path-utils";

import { CHANGE_TYPE_CLASSES, CHANGE_TYPE_LABELS } from "./constants";
import { DiffExpandedContent } from "./DiffExpandedContent";
import type { DiffItem } from "./types";

export { CHANGE_TYPE_CLASSES, CHANGE_TYPE_LABELS } from "./constants";
export { DeferredDiffViewer } from "./DeferredDiffViewer";
export type { DiffItem } from "./types";

interface DiffItemRowProps {
  item: DiffItem;
  originalContentByPath?: Record<string, string>;
  isLoadingOriginals?: boolean;
}

export const DiffItemRow = React.memo<DiffItemRowProps>(
  ({ item, originalContentByPath = {}, isLoadingOriginals = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasBeenExpanded, setHasBeenExpanded] = useState(false);

    const handleToggle = useCallback(() => {
      setIsExpanded(prev => {
        if (!prev) {
          setHasBeenExpanded(true);
        }
        return !prev;
      });
    }, []);

    const originalContent =
      item.originalContent ?? originalContentByPath[item.oldPath ?? item.path];
    const currentContent = item.currentContent ?? "";
    const isPathRelocation =
      (item.changeType === "renamed" || item.changeType === "moved") &&
      item.oldPath &&
      item.newPath;
    const itemTitle =
      item.displayLabel ?? (isPathRelocation ? `${item.oldPath} → ${item.newPath}` : item.path);
    const itemDisplayLabel =
      item.displayLabel ??
      (isPathRelocation
        ? `${formatSkillRelativePathForDisplay(item.oldPath ?? "")} → ${formatSkillRelativePathForDisplay(item.newPath ?? "")}`
        : formatSkillRelativePathForDisplay(item.path));
    const getOldValue = () => {
      if (item.changeType === "added") {
        return "";
      }
      if (originalContent !== undefined) {
        return originalContent;
      }
      return "";
    };
    const oldValue = getOldValue();
    const newValue = item.changeType === "deleted" ? "" : currentContent;
    const showUnavailableMessage =
      item.changeType !== "added" && originalContent === undefined && !isLoadingOriginals;
    const previewPath = item.newPath ?? item.oldPath ?? item.path;

    return (
      <div className="overflow-hidden rounded-md border border-gray-200">
        <button
          type="button"
          onClick={handleToggle}
          title={itemTitle}
          className="flex w-full items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 text-left hover:bg-gray-50"
        >
          <div className="flex min-w-0 items-center gap-2">
            {isExpanded ? (
              <ChevronDownIcon className="size-4 shrink-0 text-gray-500" />
            ) : (
              <ChevronRightIcon className="size-4 shrink-0 text-gray-500" />
            )}
            <div className="truncate text-sm font-medium text-gray-900">{itemDisplayLabel}</div>
          </div>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${CHANGE_TYPE_CLASSES[item.changeType]}`}
          >
            {CHANGE_TYPE_LABELS[item.changeType]}
          </span>
        </button>

        <div
          className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
            isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div
            className={`min-h-0 overflow-hidden ${!isExpanded ? "pointer-events-none" : ""}`}
            aria-hidden={!isExpanded}
          >
            {hasBeenExpanded && (
              <DiffExpandedContent
                changeType={item.changeType}
                previewPath={previewPath}
                oldValue={oldValue}
                newValue={newValue}
                showUnavailableMessage={showUnavailableMessage}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);
